import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Twitter, Linkedin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const linkClass = 'hover:text-primary transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo';

  return (
    <footer className="bg-root text-secondary border-t border-inner">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Company Info */}
          <div className="col-span-2 lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src="/logo192.png" alt="Replov logo" className="w-10 h-10 rounded-xl shadow-lg" />
              <span className="text-3xl font-bold text-primary" style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '-0.02em', fontWeight: 700 }}>
                <span style={{ color: '#fafafa' }}>repl</span>
                <span style={{ background: 'linear-gradient(135deg, #4ade80, #06b6d4, #818cf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>ov</span>
              </span>
            </div>
            <p className="text-muted mb-4 max-w-sm">
              A managed PaaS in India: deploy web apps, static sites, private services, workers, cron jobs and Redis from GitHub in Bangalore, with managed PostgreSQL through Vitibase.
            </p>

            {/* Contact Info */}
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-indigo" />
                <a href="mailto:support@replov.com" className="hover:text-primary transition">
                  support@replov.com
                </a>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 mt-6">
              <a href="https://twitter.com/replov" target="_blank" rel="noopener noreferrer"
                aria-label="Replov on X" className="w-10 h-10 bg-card rounded-full flex items-center justify-center hover:bg-indigo hover:text-white transition shadow-sm border border-inner focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com/company/replov" target="_blank" rel="noopener noreferrer"
                aria-label="Replov on LinkedIn" className="w-10 h-10 bg-card rounded-full flex items-center justify-center hover:bg-indigo hover:text-white transition shadow-sm border border-inner focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-semibold text-primary mb-4 text-lg">Product</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services" className={linkClass}>Features</Link></li>
              <li><Link to="/pricing" className={linkClass}>Pricing</Link></li>
              <li><Link to="/deploy" className={linkClass}>Deployment guides</Link></li>
              <li><Link to="/compare" className={linkClass}>Compare platforms</Link></li>
              <li><Link to="/managed-paas-india" className={linkClass}>Managed PaaS in India</Link></li>
              <li><Link to="/redis" className={linkClass}>Managed Redis</Link></li>
              <li><Link to="/ai-search" className={linkClass}>AI Search check</Link></li>
              <li><Link to="/docs/getting-started/introduction" className={linkClass}>Documentation</Link></li>
              <li><a href="https://vitibase.com/" target="_blank" rel="noopener noreferrer" className={linkClass}>Vitibase</a></li>
            </ul>
          </div>

          {/* Hackathon */}
          <div>
            <h4 className="font-semibold text-primary mb-4 text-lg">Hackathon</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/hackathon" className={linkClass}>Overview</Link></li>
              <li><Link to="/hackathon/register" className={linkClass}>Register</Link></li>
              <li><Link to="/hackathon/schedule" className={linkClass}>Schedule</Link></li>
              <li><Link to="/hackathon/rules" className={linkClass}>Rules</Link></li>
              <li><Link to="/hackathon/faq" className={linkClass}>FAQ</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-primary mb-4 text-lg">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className={linkClass}>About Us</Link></li>
              <li><Link to="/contact" className={linkClass}>Contact</Link></li>
              <li><Link to="/status" className={linkClass}>System status</Link></li>
              <li><Link to="/careers" className={linkClass}>Careers</Link></li>
              <li><Link to="/blog" className={linkClass}>Guides</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-primary mb-4 text-lg">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/terms-of-service" className={linkClass}>Terms of Service</Link></li>
              <li><Link to="/privacy-policy" className={linkClass}>Privacy Policy</Link></li>
              <li><Link to="/refund-policy" className={linkClass}>Refund Policy</Link></li>
              <li><Link to="/shipping-delivery" className={linkClass}>Shipping & Delivery</Link></li>
              <li><Link to="/cancellation-policy" className={linkClass}>Cancellation Policy</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-inner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-secondary">
            <div>
              © {currentYear} Replov. All rights reserved.
            </div>
            <div className="flex flex-wrap gap-6 justify-center">
              <Link to="/terms-of-service" className={linkClass}>Terms</Link>
              <Link to="/privacy-policy" className={linkClass}>Privacy</Link>
              <Link to="/refund-policy" className={linkClass}>Refunds</Link>
              <Link to="/contact" className={linkClass}>Contact</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
